import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { Exercise, Equipment } from '@/types'

// Extended types for library management
export interface CustomExercise extends Omit<Exercise, 'id'> {
  id?: string
  createdBy: string
  isCustom: boolean
  createdAt: Date
  updatedAt: Date
  tags?: string[]
  videoUrl?: string
  imageUrl?: string
  difficulty?: 'beginner' | 'intermediate' | 'advanced'
  targetReps?: string
  targetSets?: number
  restTime?: number
}

export interface CustomEquipment {
  id: string
  name: string
  type: Equipment
  description?: string
  brand?: string
  model?: string
  purchaseUrl?: string
  imageUrl?: string
  createdBy: string
  isCustom: boolean
  createdAt: Date
  updatedAt: Date
  tags?: string[]
}

interface LibraryFilters {
  search?: string
  muscleGroup?: string
  equipment?: string
  category?: string
  difficulty?: string
  createdBy?: string
  isCustom?: boolean
}

interface LibraryStore {
  // Exercises
  exercises: CustomExercise[]
  filteredExercises: CustomExercise[]
  exercisesLoading: boolean
  exerciseFilters: LibraryFilters
  
  // Equipment
  equipment: CustomEquipment[]
  filteredEquipment: CustomEquipment[]
  equipmentLoading: boolean
  equipmentFilters: LibraryFilters
  
  // UI State
  activeTab: 'exercises' | 'equipment'
  selectedExercise: CustomExercise | null
  selectedEquipment: CustomEquipment | null
  isCreateExerciseModalOpen: boolean
  isCreateEquipmentModalOpen: boolean
  isEditModalOpen: boolean
  
  // Actions - Exercises
  setExercises: (exercises: CustomExercise[]) => void
  addExercise: (exercise: CustomExercise) => void
  updateExercise: (id: string, updates: Partial<CustomExercise>) => void
  deleteExercise: (id: string) => void
  setExerciseFilters: (filters: Partial<LibraryFilters>) => void
  filterExercises: () => void
  
  // Actions - Equipment
  setEquipment: (equipment: CustomEquipment[]) => void
  addEquipment: (equipment: CustomEquipment) => void
  updateEquipment: (id: string, updates: Partial<CustomEquipment>) => void
  deleteEquipment: (id: string) => void
  setEquipmentFilters: (filters: Partial<LibraryFilters>) => void
  filterEquipment: () => void
  
  // Actions - UI State
  setActiveTab: (tab: 'exercises' | 'equipment') => void
  setSelectedExercise: (exercise: CustomExercise | null) => void
  setSelectedEquipment: (equipment: CustomEquipment | null) => void
  setIsCreateExerciseModalOpen: (open: boolean) => void
  setIsCreateEquipmentModalOpen: (open: boolean) => void
  setIsEditModalOpen: (open: boolean) => void
  
  // Actions - Loading States
  setExercisesLoading: (loading: boolean) => void
  setEquipmentLoading: (loading: boolean) => void
  
  // Complex Actions
  loadLibraryData: () => Promise<void>
  duplicateExercise: (exerciseId: string) => void
  duplicateEquipment: (equipmentId: string) => void
  resetStore: () => void
}

// Helper functions for mock data
/*
const getMockExercises = (): CustomExercise[] => [
  {
    id: 'ex-custom-1',
    name: 'Bulgarian Split Squats',
    category: 'compound',
    muscleGroups: ['quads', 'glutes', 'hamstrings'],
    equipment: ['dumbbells'],
    instructions: [
      'Stand 2 feet in front of a bench',
      'Place back foot on bench',
      'Lower into lunge position',
      'Drive through front heel to return'
    ],
    modifications: [],
    progressions: [],
    personalRecords: [],
    createdBy: 'coach-001',
    isCustom: true,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
    difficulty: 'intermediate',
    targetReps: '10-12',
    targetSets: 3,
    restTime: 90,
    tags: ['unilateral', 'legs', 'balance']
  },
  {
    id: 'ex-custom-2',
    name: 'Band Pull-Aparts',
    category: 'isolation',
    muscleGroups: ['shoulders', 'back'],
    equipment: ['resistance-bands'],
    instructions: [
      'Hold resistance band at chest height',
      'Pull band apart by squeezing shoulder blades',
      'Control the return'
    ],
    modifications: [],
    progressions: [],
    personalRecords: [],
    createdBy: 'coach-001',
    isCustom: true,
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20'),
    difficulty: 'beginner',
    targetReps: '15-20',
    targetSets: 2,
    restTime: 30,
    tags: ['activation', 'posture', 'warm-up']
  }
]

const getMockEquipment = (): CustomEquipment[] => [
  {
    id: 'eq-custom-1',
    name: 'TRX Suspension Trainer',
    type: 'suspension-trainer',
    description: 'Professional grade suspension trainer for bodyweight exercises',
    brand: 'TRX',
    model: 'TRX Pro',
    createdBy: 'coach-001',
    isCustom: true,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-10'),
    tags: ['suspension', 'functional', 'portable']
  },
  {
    id: 'eq-custom-2',
    name: 'Concept2 Model D',
    type: 'rowing-machine',
    description: 'Professional rowing machine for cardio and full-body workouts',
    brand: 'Concept2',
    model: 'Model D',
    createdBy: 'coach-001',
    isCustom: true,
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-12'),
    tags: ['cardio', 'full-body', 'endurance']
  }
]
*/

const initialState = {
  exercises: [],
  filteredExercises: [],
  exercisesLoading: false,
  exerciseFilters: {},
  equipment: [],
  filteredEquipment: [],
  equipmentLoading: false,
  equipmentFilters: {},
  activeTab: 'exercises' as const,
  selectedExercise: null,
  selectedEquipment: null,
  isCreateExerciseModalOpen: false,
  isCreateEquipmentModalOpen: false,
  isEditModalOpen: false,
}

export const useLibraryStore = create<LibraryStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Exercise Actions
      setExercises: (exercises) => {
        set({ exercises })
        get().filterExercises()
      },
      
      addExercise: (exercise) => {
        const exercises = [...get().exercises, exercise]
        set({ exercises })
        get().filterExercises()
      },
      
      updateExercise: (id, updates) => {
        const exercises = get().exercises.map(ex => 
          ex.id === id ? { ...ex, ...updates, updatedAt: new Date() } : ex
        )
        set({ exercises })
        get().filterExercises()
      },
      
      deleteExercise: (id) => {
        const exercises = get().exercises.filter(ex => ex.id !== id)
        set({ exercises })
        get().filterExercises()
      },
      
      setExerciseFilters: (filters) => {
        const newFilters = { ...get().exerciseFilters, ...filters }
        set({ exerciseFilters: newFilters })
        get().filterExercises()
      },
      
      filterExercises: () => {
        const { exercises, exerciseFilters } = get()
        let filtered = [...exercises]
        
        if (exerciseFilters.search) {
          const search = exerciseFilters.search.toLowerCase()
          filtered = filtered.filter(ex => 
            ex.name.toLowerCase().includes(search) ||
            ex.instructions?.some(inst => inst.toLowerCase().includes(search)) ||
            ex.tags?.some(tag => tag.toLowerCase().includes(search))
          )
        }
        
        if (exerciseFilters.muscleGroup) {
          filtered = filtered.filter(ex => 
            ex.muscleGroups?.includes(exerciseFilters.muscleGroup as any)
          )
        }
        
        if (exerciseFilters.equipment) {
          filtered = filtered.filter(ex => 
            ex.equipment?.includes(exerciseFilters.equipment as any)
          )
        }
        
        if (exerciseFilters.category) {
          filtered = filtered.filter(ex => ex.category === exerciseFilters.category)
        }
        
        if (exerciseFilters.difficulty) {
          filtered = filtered.filter(ex => ex.difficulty === exerciseFilters.difficulty)
        }
        
        if (exerciseFilters.isCustom !== undefined) {
          filtered = filtered.filter(ex => ex.isCustom === exerciseFilters.isCustom)
        }
        
        set({ filteredExercises: filtered })
      },

      // Equipment Actions
      setEquipment: (equipment) => {
        set({ equipment })
        get().filterEquipment()
      },
      
      addEquipment: (equipment) => {
        const equipmentList = [...get().equipment, equipment]
        set({ equipment: equipmentList })
        get().filterEquipment()
      },
      
      updateEquipment: (id, updates) => {
        const equipment = get().equipment.map(eq => 
          eq.id === id ? { ...eq, ...updates, updatedAt: new Date() } : eq
        )
        set({ equipment })
        get().filterEquipment()
      },
      
      deleteEquipment: (id) => {
        const equipment = get().equipment.filter(eq => eq.id !== id)
        set({ equipment })
        get().filterEquipment()
      },
      
      setEquipmentFilters: (filters) => {
        const newFilters = { ...get().equipmentFilters, ...filters }
        set({ equipmentFilters: newFilters })
        get().filterEquipment()
      },
      
      filterEquipment: () => {
        const { equipment, equipmentFilters } = get()
        let filtered = [...equipment]
        
        if (equipmentFilters.search) {
          const search = equipmentFilters.search.toLowerCase()
          filtered = filtered.filter(eq => 
            eq.name.toLowerCase().includes(search) ||
            eq.description?.toLowerCase().includes(search) ||
            eq.brand?.toLowerCase().includes(search) ||
            eq.tags?.some(tag => tag.toLowerCase().includes(search))
          )
        }
        
        if (equipmentFilters.equipment) {
          filtered = filtered.filter(eq => eq.type === equipmentFilters.equipment)
        }
        
        if (equipmentFilters.isCustom !== undefined) {
          filtered = filtered.filter(eq => eq.isCustom === equipmentFilters.isCustom)
        }
        
        set({ filteredEquipment: filtered })
      },

      // UI State Actions
      setActiveTab: (tab) => set({ activeTab: tab }),
      setSelectedExercise: (exercise) => set({ selectedExercise: exercise }),
      setSelectedEquipment: (equipment) => set({ selectedEquipment: equipment }),
      setIsCreateExerciseModalOpen: (open) => set({ isCreateExerciseModalOpen: open }),
      setIsCreateEquipmentModalOpen: (open) => set({ isCreateEquipmentModalOpen: open }),
      setIsEditModalOpen: (open) => set({ isEditModalOpen: open }),

      // Loading State Actions
      setExercisesLoading: (loading) => set({ exercisesLoading: loading }),
      setEquipmentLoading: (loading) => set({ equipmentLoading: loading }),

      // Complex Actions
      loadLibraryData: async () => {
        set({ exercisesLoading: true, equipmentLoading: true })
        
        try {
          // Load exercises and equipment from API
          const { LibraryService } = await import('@/services/libraryService')
          
          // Try to load from backend first
          const [exercisesResult, equipmentResult] = await Promise.allSettled([
            LibraryService.getExercises({}, 1, 100),
            LibraryService.getEquipment({}, 1, 100)
          ])
          
          // Process exercises
          if (exercisesResult.status === 'fulfilled' && exercisesResult.value.data) {
            const backendExercises = exercisesResult.value.data.map(ex => ({
              ...ex,
              isCustom: ex.source === 'coach',
              createdBy: ex.coachId || 'global',
              createdAt: new Date(ex.createdAt),
              updatedAt: new Date(ex.updatedAt)
            }))
            get().setExercises(backendExercises.map(ex => ({
              ...ex,
              muscleGroups: (ex as any).muscleGroups || [],
              category: (ex as any).category || 'strength',
              difficulty: (ex as any).difficulty === 1 ? 'beginner' : 
                         (ex as any).difficulty === 2 ? 'intermediate' : 
                         (ex as any).difficulty === 3 ? 'advanced' : undefined,
              sets: undefined, // Override the number type
              equipment: [], // Override the object type with array
              modifications: [],
              progressions: [],
              personalRecords: []
            })))
          } else {
            console.error('❌ Failed to load exercises from backend')
            throw new Error('Failed to load exercises from backend API')
          }
          
          // Process equipment
          if (equipmentResult.status === 'fulfilled' && equipmentResult.value.data) {
            const backendEquipment = equipmentResult.value.data.map(eq => ({
              ...eq,
              isCustom: eq.source === 'coach',
              createdBy: eq.coachId || 'global',
              createdAt: new Date(eq.createdAt),
              updatedAt: new Date(eq.updatedAt)
            }))
            get().setEquipment(backendEquipment.map(eq => ({
              ...eq,
              type: eq.category?.toLowerCase() as any || 'strength'
            })))
          } else {
            console.error('❌ Failed to load equipment from backend')
            throw new Error('Failed to load equipment from backend API')
          }
          
        } catch (error) {
          console.error('❌ Failed to load library data from backend:', error)
          // NO MOCK DATA - show real error
          toast.error(`Library loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
          throw error
        } finally {
          set({ exercisesLoading: false, equipmentLoading: false })
        }
      },
      
      duplicateExercise: (exerciseId) => {
        const exercise = get().exercises.find(ex => ex.id === exerciseId)
        if (exercise) {
          const duplicated: CustomExercise = {
            ...exercise,
            id: `${exercise.id}-copy-${Date.now()}`,
            name: `${exercise.name} (Copy)`,
            createdAt: new Date(),
            updatedAt: new Date()
          }
          get().addExercise(duplicated)
        }
      },
      
      duplicateEquipment: (equipmentId) => {
        const equipment = get().equipment.find(eq => eq.id === equipmentId)
        if (equipment) {
          const duplicated: CustomEquipment = {
            ...equipment,
            id: `${equipment.id}-copy-${Date.now()}`,
            name: `${equipment.name} (Copy)`,
            createdAt: new Date(),
            updatedAt: new Date()
          }
          get().addEquipment(duplicated)
        }
      },
      
      resetStore: () => set(initialState)
    }),
    {
      name: 'library-store'
    }
  )
)